import React from "react";
import store from "../store";
import { Card, Typography, List, Space } from "antd";
import { TrophyOutlined, CrownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Avatar from './Avatar';

const { Title, Text } = Typography;

interface ScoreBoardState {
  scores: any[];
}

interface ScoreBoardProps {
  t: any;
}

class ScoreBoardComponent extends React.Component<ScoreBoardProps, ScoreBoardState> {
  constructor(props: ScoreBoardProps) {
    super(props);
    this.state = {
      scores: []
    }
  }

  componentDidMount(): void {
    // Initialize with current store state
    const currentState = store.getState();
    this.setState({
      scores: this.getScores(currentState)
    });

    // Subscribe to future changes
    const unsubscribe = store.subscribe(() => {
      let state = store.getState();
      this.setState({
        scores: this.getScores(state)
      })
    })
  }

  getScores(state: any): any[] {
    let scores: string[] = state.kingGame.gameState.scores || [];
    
    let obj: { name: string, score: string, negatives: string, positives: string }[] = [];
    if (scores) {
      scores.forEach(s => {
        const strings = s.split("#");
        obj.push({
          name: strings[0],
          score: strings[1],
          negatives: strings[2],
          positives: strings[3]
        });
      });
    }
    
    return obj;
  }


  render() {
    const { scores } = this.state;
    const { t } = this.props;

    // Sort scores by points (highest first)
    const sortedScores = [...scores].sort((a, b) => parseInt(b.score) - parseInt(a.score));

    return (
      <Card
        className="glass-effect"
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          background: 'rgba(30, 41, 59, 0.8)',
          backdropFilter: 'blur(10px)'
        }}
        title={
          <Space>
            <TrophyOutlined style={{ color: 'var(--secondary-color)' }} />
            <Title level={4} style={{ margin: 0, color: 'var(--text-primary)' }}>
              {t('score.scoreboard')}
            </Title>
          </Space>
        }
      >

        {/* Scores List */}
        <List
          dataSource={sortedScores}
          style={{
            maxHeight: '250px',
            overflowY: 'auto'
          }}
          renderItem={(item, index) => (
            <List.Item
              style={{
                padding: '8px 0',
                borderBottom: index < sortedScores.length - 1 ? '1px solid var(--border-color)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                {/* Position Badge */}
                <div style={{ position: 'relative' }}>
                  {/* Position Badge */}
                  <div style={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? 'var(--secondary-color)' : 
                                   index === 1 ? 'var(--text-muted)' : 
                                   index === 2 ? '#cd7f32' : 'var(--bg-tertiary)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    border: '1px solid var(--bg-secondary)'
                  }}>
                    {index + 1}
                  </div>
                  
                  {/* Avatar */}
                  <Avatar 
                    name={item.name}
                    size={32}
                    isCurrentPlayer={index === 0}
                    showBorder={true}
                  />
                </div>

                {/* Player Info */}
                <div style={{ flex: 1 }}>
                  <Text style={{ 
                    color: 'var(--text-primary)', 
                    fontWeight: index === 0 ? 700 : 500,
                    fontSize: '0.9rem',
                    display: 'block',
                    lineHeight: 1.3
                  }}>
                    {item.name}
                    {index === 0 && (
                      <CrownOutlined 
                        style={{ 
                          marginLeft: 6, 
                          color: 'var(--secondary-color)',
                          fontSize: '0.9rem'
                        }} 
                      />
                    )}
                  </Text>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginTop: '2px'
                  }}>
                    {/* Negative rounds indicators */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '2px' 
                    }}>
                      {[1, 2, 3].map((index) => (
                        <div
                          key={`neg-${index}`}
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: index <= (item.negatives ? item.negatives.length : 0)
                              ? '#ef4444' 
                              : 'var(--bg-primary)',
                            border: '1px solid #ef4444',
                            transition: 'all 0.3s ease',
                            transform: index <= (item.negatives ? item.negatives.length : 0) ? 'scale(1.1)' : 'scale(1)',
                            boxShadow: index <= (item.negatives ? item.negatives.length : 0) ? '0 0 4px rgba(239, 68, 68, 0.5)' : 'none'
                          }}
                        />
                      ))}
                    </div>
                    
                    {/* Positive rounds indicators */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '2px' 
                    }}>
                      {[1, 2].map((index) => (
                        <div
                          key={`pos-${index}`}
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: index <= (item.positives ? item.positives.length : 0)
                              ? '#22c55e' 
                              : 'var(--bg-primary)',
                            border: '1px solid #22c55e',
                            transition: 'all 0.3s ease',
                            transform: index <= (item.positives ? item.positives.length : 0) ? 'scale(1.1)' : 'scale(1)',
                            boxShadow: index <= (item.positives ? item.positives.length : 0) ? '0 0 4px rgba(34, 197, 94, 0.5)' : 'none'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Points */}
                <div style={{ textAlign: 'right' }}>
                  <Text style={{ 
                    color: index === 0 ? 'var(--secondary-color)' : 'var(--text-primary)', 
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    display: 'block',
                    lineHeight: 1.2
                  }}>
                    {item.score}
                  </Text>
                  <Text style={{ color: 'var(--text-muted)', fontSize: '0.7rem', display: 'block' }}>
                    {t('score.pts')}
                  </Text>
                </div>
              </div>
            </List.Item>
          )}
          locale={{
            emptyText: (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <Text style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  {t('score.noScoresYet')}
                </Text>
                <br />
                <Text style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  {t('score.scoresWillAppear')}
                </Text>
              </div>
            )
          }}
        />
      </Card>
    );
  }
}

// Functional wrapper component to use hooks  
const ScoreBoard = () => {
  const { t } = useTranslation();
  return <ScoreBoardComponent t={t} />;
};

export default ScoreBoard;